import { useEffect, useMemo, useState } from "react";

import {
  Loader,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button, Input } from "@transfr-inc/dashboard-components/forms";

import { ColorPicker } from "@components/custom/dropdowns";
import {
  useCreateOrganizationTag,
  useUpdateOrganizationTag,
} from "@hooks/queries";
import { TagType } from "@models";

export const TagFormSlide = ({ organizationCode, tag, onBack, onDone }) => {
  const [values, setValues] = useState();

  const {
    mutateAsync: createDeviceTag,
    error: createDeviceTagError,
    isPending: creatingDeviceTag,
    reset: resetCreateDeviceTag,
  } = useCreateOrganizationTag(organizationCode);

  const {
    mutateAsync: updateDeviceTag,
    error: updateDeviceTagError,
    isPending: updatingDeviceTag,
    reset: resetUpdateDeviceTag,
  } = useUpdateOrganizationTag(organizationCode);

  const config = useMemo(() => {
    if (!tag) {
      return {
        title: "Create Tag",
        icon: ["fa-regular", "plus"],
        buttonText: "Create",
        mutationFn: createDeviceTag,
      };
    } else {
      return {
        title: "Edit Tag",
        icon: ["fa-regular", "edit"],
        buttonText: "Done",
        mutationFn: updateDeviceTag,
      };
    }
  }, [tag, createDeviceTag, updateDeviceTag]);

  const errorMessage = useMemo(() => {
    if (createDeviceTagError || updateDeviceTagError) {
      const createErrorDetail = createDeviceTagError?.data?.detail;
      const updateErrorDetail = updateDeviceTagError?.data?.detail;
      return (
        createErrorDetail ||
        updateErrorDetail ||
        "Something went wrong. Please try again."
      );
    }
  }, [createDeviceTagError, updateDeviceTagError]);

  useEffect(() => {
    setValues(tag ?? {});
    resetCreateDeviceTag();
    resetUpdateDeviceTag();
  }, [tag]);

  const handleBack = () => {
    resetCreateDeviceTag();
    resetUpdateDeviceTag();
    setValues();
    onBack?.();
  };

  const handleSubmit = async () => {
    await config.mutationFn({ ...values, type: TagType.DEVICE });
    setValues();
    onDone?.();
  };

  return (
    <>
      {(creatingDeviceTag || updatingDeviceTag) && <Loader overlay />}
      <ModalHeader
        className="blue-icon"
        title={config.title}
        icon={config.icon}
      />
      <ModalBody>
        {errorMessage && (
          <Notification type={NotificationType.error}>
            {errorMessage}
          </Notification>
        )}
        <div className="form-container">
          <div className="color-picker-container">
            <span>Color</span>
            <ColorPicker
              value={values?.color}
              onChange={(color) => setValues({ ...values, color })}
            />
          </div>
          <Input
            label="Title"
            name="tag-title"
            value={values?.value ?? ""}
            onChange={(e) => setValues({ ...values, value: e.target.value })}
            maxLength={100}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleBack}>Cancel</Button>
        <Button primary onClick={handleSubmit} disabled={!values?.value}>
          {config.buttonText}
        </Button>
      </ModalFooter>
    </>
  );
};
